import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AdFormat } from '@/types';
import { DEFAULT_CREATIVE_CONTENT, DEFAULT_HEADLINES, type CreativeContent } from '@/types/creative';
import { DEFAULT_FORMAT, getFormatById } from '@/components/providers';

interface EditorState {
  // Current selections
  selectedFormatId: string;
  selectedFormat: AdFormat;

  // Content
  content: CreativeContent;

  // View state
  zoom: number;
  panOffset: { x: number; y: number };
  showGrid: boolean;

  // Animation state
  isPlaying: boolean;
  currentTime: number;
  duration: number;

  // Active panel (null = collapsed)
  activePanel: 'format' | 'template' | 'content' | 'assets' | 'responsive' | 'properties' | 'preview' | 'animate' | 'export' | 'layers' | null;
}

interface EditorActions {
  // Format actions
  setFormat: (format: AdFormat) => void;
  setFormatById: (id: string) => void;

  // Content actions
  setContent: (content: Partial<CreativeContent>) => void;
  setHeadlineByIndex: (index: number | 'custom') => void;
  setCustomHeadline: (headline: string) => void;
  setCta: (cta: string) => void;
  toggleShowSubhead: () => void;
  toggleShowLogo: () => void;
  toggleShowBadges: () => void;

  // View actions
  setZoom: (zoom: number) => void;
  setPanOffset: (offset: { x: number; y: number }) => void;
  panBy: (deltaX: number, deltaY: number) => void;
  resetPan: () => void;
  toggleGrid: () => void;
  fitToView: (containerWidth: number, containerHeight: number) => void;

  // Animation actions
  setPlaying: (playing: boolean) => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;

  // Panel actions
  setActivePanel: (panel: EditorState['activePanel']) => void;

  // Reset
  reset: () => void;
}

const initialState: EditorState = {
  selectedFormatId: DEFAULT_FORMAT.id,
  selectedFormat: DEFAULT_FORMAT,
  content: DEFAULT_CREATIVE_CONTENT,
  zoom: 1,
  panOffset: { x: 0, y: 0 },
  showGrid: true,
  isPlaying: false,
  currentTime: 0,
  duration: 3000,
  activePanel: 'layers',
};

export const useEditorStore = create<EditorState & EditorActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      setFormat: (format) =>
        set({ selectedFormat: format, selectedFormatId: format.id }),

      setFormatById: (id) => {
        const format = getFormatById(id);
        if (format) {
          set({ selectedFormat: format, selectedFormatId: id });
        }
      },

      setContent: (content) =>
        set((state) => ({
          content: { ...state.content, ...content },
        })),

      setHeadlineByIndex: (index) => {
        if (index === 'custom') {
          set((state) => ({
            content: {
              ...state.content,
              headlineIdx: 'custom',
            },
          }));
        } else {
          const preset = DEFAULT_HEADLINES[index];
          if (preset) {
            set((state) => ({
              content: {
                ...state.content,
                headlineIdx: index,
                headline: preset.headline,
                subhead: preset.subhead,
              },
            }));
          }
        }
      },

      setCustomHeadline: (headline) =>
        set((state) => ({
          content: {
            ...state.content,
            headline,
            customHeadline: headline,
          },
        })),

      setCta: (cta) =>
        set((state) => ({
          content: { ...state.content, cta },
        })),

      toggleShowSubhead: () =>
        set((state) => ({
          content: { ...state.content, showSubhead: !state.content.showSubhead },
        })),

      toggleShowLogo: () =>
        set((state) => ({
          content: { ...state.content, showLogo: !state.content.showLogo },
        })),

      toggleShowBadges: () =>
        set((state) => ({
          content: { ...state.content, showBadges: !state.content.showBadges },
        })),

      setZoom: (zoom) => set({ zoom: Math.max(0.25, Math.min(3, zoom)) }),

      setPanOffset: (panOffset) => set({ panOffset }),

      panBy: (deltaX, deltaY) =>
        set((state) => ({
          panOffset: {
            x: state.panOffset.x + deltaX,
            y: state.panOffset.y + deltaY,
          },
        })),

      resetPan: () => set({ panOffset: { x: 0, y: 0 } }),

      toggleGrid: () => set((state) => ({ showGrid: !state.showGrid })),

      fitToView: (containerWidth, containerHeight) => {
        const { selectedFormat } = get();
        const padding = 100;
        const availableWidth = Math.max(containerWidth - padding, 100);
        const availableHeight = Math.max(containerHeight - padding, 100);
        const calculatedZoom = Math.min(
          availableWidth / selectedFormat.width,
          availableHeight / selectedFormat.height,
          1
        );
        // Clamp zoom to valid range (0.25 to 3)
        const zoom = Math.max(0.25, Math.min(3, calculatedZoom));
        // Reset pan when fitting to view
        set({ zoom, panOffset: { x: 0, y: 0 } });
      },

      setPlaying: (isPlaying) => set({ isPlaying }),

      setCurrentTime: (currentTime) => set({ currentTime }),

      setDuration: (duration) => set({ duration }),

      setActivePanel: (activePanel) => set({ activePanel }),

      reset: () => set(initialState),
    }),
    {
      name: 'adstudio-editor',
      partialize: (state) => ({
        selectedFormatId: state.selectedFormatId,
        content: state.content,
        showGrid: state.showGrid,
      }),
    }
  )
);

// Derived state helpers
export function useCurrentHeadline() {
  const content = useEditorStore((s) => s.content);
  if (content.headlineIdx === 'custom') {
    return content.customHeadline || content.headline;
  }
  return DEFAULT_HEADLINES[content.headlineIdx]?.headline || content.headline;
}

export function useCurrentSubhead() {
  const content = useEditorStore((s) => s.content);
  if (!content.showSubhead) return null;
  if (content.headlineIdx === 'custom') {
    return DEFAULT_HEADLINES[0].subhead;
  }
  return DEFAULT_HEADLINES[content.headlineIdx]?.subhead || null;
}
